<template>
  <large-data-autocomplete
    v-if="items.length > 150 && multiple && toggleAll"
    v-bind="{...$props, ...$attrs}"
    :style="inputRadius"
    :value="value"
    v-on="$listeners"
  >
  </large-data-autocomplete>

  <v-autocomplete
    v-else
    ref="autocomplete"
    outlined
    class="zoox-component zoox-autocomplete"
    no-filter
    :style="inputRadius"
    :clearable="clearable"
    :class="{small, chips}"
    :color="color"
    :deletable-chips="deletableChips"
    :chips="chips"
    :dense="isDense"
    :small-chips="chips"
    :multiple="multiple"
    :item-text="itemText"
    :item-value="itemValue"
    :items="filteredItems"
    :value="value"
    :append-icon="appendIcon"
    :hint="hint"
    :rules="rules"
    :label="label"
    :search-input="searchInput"
    :hide-details="hideDetails"
    :no-data-text="noDataText || _noDataText"
    v-bind="$attrs"
    v-on="$listeners"
    @update:search-input="onSearchInput"
  >
    <template v-if="multiple && !$scopedSlots.item" #item="{attrs, item}">
      <v-list-item-action>
        <z-icon>
          {{
            attrs.inputValue
              ? '$check_box_rounded'
              : '$check_box_outline_blank_rounded'
          }}
        </z-icon>
      </v-list-item-action>

      <v-list-item-content>
        <v-list-item-title>
          {{ item[itemText] || item }}
        </v-list-item-title>
      </v-list-item-content>
    </template>

    <template v-else-if="$scopedSlots.item" #item="{item, ...props}">
      <!--
         @slot Define uma aparencia customizada para o item
            @binding {object} item Objeto referente ao item passado
      -->
      <slot name="item" :item="item" v-bind="props"></slot>
    </template>

    <template v-if="shouldRenderSelectionSlot" #selection="{item, ...props}">
      <!-- @slot Define uma aparencia customizada para o selection 
            @binding {object} item Objeto referente ao item passado
      -->
      <slot name="selection" :item="item" v-bind="props">
        <z-chip
          v-if="chips"
          :x-small="!large"
          :color="color || 'primary'"
          selected
          :text="item.text"
        />
      </slot>

      <template
        v-if="truncateValues && !chips && multiple && props.index === 0"
      >
        <div class="truncate">
          {{ getAllSelectedValues }}
        </div>
      </template>
    </template>

    <template v-if="multiple && toggleAll" #prepend-item>
      <div v-ripple tabindex="0" class="zoox-select-all" @click="onToggleAll">
        <div class="action">
          <z-icon :color="hasSelectionInFilteredItems ? 'primary' : ''">{{
            hasSelectionInFilteredItems
              ? '$check_box_rounded'
              : '$check_box_outline_blank_rounded'
          }}</z-icon>
        </div>

        <div :style="{fontSize: isDense ? '.8125rem' : '1rem'}" class="content">
          {{
            hasSelectionInFilteredItems ? $t('uncheck_all') : $t('check_all')
          }}
        </div>
      </div>
    </template>

    <template #append>
      <slot name="append"></slot>
    </template>

    <template #append-item>
      <slot name="append-item"></slot>
    </template>

    <template #append-outer>
      <slot name="append-outer"></slot>
    </template>

    <template #prepend>
      <slot name="prepend"></slot>
    </template>

    <template #prepend-inner>
      <slot name="prepend-inner"></slot>
    </template>
  </v-autocomplete>
</template>

<script>
import LargeDataAutocomplete from './private/LargeDataAutocomplete.vue'

/**
 * @displayName Autocomplete
 */
export default {
  name: 'ZAutocomplete',

  components: {
    LargeDataAutocomplete,
  },
  model: {
    prop: 'value',
    event: 'change',
  },

  props: {
    /**
     * Especifica quais itens estarão no autocomplete
     *
     * @typedef Object {text:string,value:any}
     */
    items: {
      type: Array,
      required: true,
    },
    /**
     * Customiza a chave 'text' no array de itens
     */
    itemText: {
      type: String,
      required: false,
      default: 'text',
    },
    /**
     * Aplica o estilo de chip no item selecionado
     */
    chips: {
      type: Boolean,
      default: false,
    },
    /**
     *  Aplica um icone para remover o chip quando a prop `chips` é usada
     */
    deletableChips: {
      type: Boolean,
      default: false,
    },
    /**
     * Quando true, multiplos itens podem ser selecionados
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para indicar se o autocomplete é clearable
     * ou não
     */
    clearable: {
      type: Boolean,
      default: true,
    },
    /**
     * Customiza a chave 'value' no array de itens
     */
    itemValue: {
      type: String,
      required: false,
      default: 'value',
    },
    /**
     * Determina um valor para o input
     *
     * @modelName value
     */
    value: {
      type: [Number, String, Object, Array],
      required: false,
    },
    /**
     * Define o ícone em frente ao input
     */
    appendIcon: {
      type: String,
      required: false,
    },
    /**
     * Hint para o input
     */
    hint: {
      type: String,
      required: false,
    },
    /**
     * Label do auto complete
     */
    label: {
      type: String,
      required: false,
    },
    /**
     * Regras associadas com o input
     */
    rules: {
      type: Array,
      required: false,
    },
    /**
     * Flag para esconder div de mensagens e hint
     */
    hideDetails: {
      type: [Boolean, String],
      required: false,
    },
    /**
     * Valor do input de busca
     */
    searchInput: {
      type: String,
      default: '',
    },
    /**
     * Define o texto quando não existe dados disponiveis
     */
    noDataText: {
      type: String,
    },
    /**
     * Flag para indicar que o input deve ser pequeno (defaults: {mobile: false, desktop: false})
     */
    small: {
      type: Boolean,
      required: false,
    },
    /**
     * Flag para indicar que o input deve ser medio (defaults: {mobile: false, desktop: true})
     */
    medium: {
      type: Boolean,
      required: false,
    },
    /**
     * Flag para indicar que o input deve ser grande (defaults: {mobile: true, desktop: false})
     */
    large: {
      type: Boolean,
      required: false,
    },
    /**
     * Cor do input e dos chips se for usado no modo chips
     */
    color: {
      type: String,
      default: 'primary',
    },

    /* Aplica funcionalidade de "selecionar todos" quando a prop `multiple` é usada */
    toggleAll: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Trunca os valores do select quando a prop `multiple` é true e a prop `chips` é falsa
     */
    truncateValues: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Define o border-radius do input
     */
    radius: {
      type: Number,
      required: false,
    },

    filter: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      isMounted: false,
      internalSearch: this.searchInput,
    }
  },

  computed: {
    filteredItems() {
      if (!this.internalSearch) return this.items

      return this.items.filter((item) => {
        const value = item[this.itemText]
        const text = value !== null ? String(value) : ''

        return this.filterTextItems(item, String(this.internalSearch), text)
      })
    },

    hasSelectionInFilteredItems() {
      if (!this.isMounted) return

      return this.$refs.autocomplete.filteredItems.some((item) => {
        return this.$refs.autocomplete.selectedItems.includes(item)
      })
    },

    _noDataText() {
      return this.$t('no_data_available')
    },
    isDense() {
      const isMobile = this.$vuetify.breakpoint.xs

      if (!this.small && !this.medium && !this.large) {
        return isMobile ? false : true
      }

      if (this.large) {
        return false
      }

      return this.small || this.medium
    },

    itemsAreStrings() {
      return this.items.every((item) => typeof item === 'string')
    },

    shouldRenderSelectionSlot() {
      return (
        this.$scopedSlots.selection ||
        this.chips ||
        (this.truncateValues && this.multiple && !this.chips)
      )
    },

    getAllSelectedValues() {
      if (this.itemsAreStrings) return this.value.join(', ')

      return this.items
        .filter((item) => this.value.includes(item[this.itemValue]))
        .map((item) => item[this.itemText])
        .join(', ')
    },

    inputRadius() {
      if (this.radius) {
        return `border-radius: ${this.radius}px !important`
      }

      if (this.small) {
        return 'border-radius: var(--component-input-sm-radius) !important'
      }

      return 'border-radius: var(--component-input-md-radius) !important'
    },
  },

  watch: {
    searchInput(val) {
      this.internalSearch = val
    },

    internalSearch() {
      this.updateInernalAutocompleteSelectedItems()
    },

    value() {
      this.updateInernalAutocompleteSelectedItems()
    },
  },

  mounted() {
    this.isMounted = true
  },
  methods: {
    /**
     * Como estamos utilizando nosso próprio filtro,
     * os itens fornecidos para o componente do Vuetify estão sempre mudando.
     *
     * Isso faz com que ele perca a referência dos itens selecionados,
     * resultando no desaparecimento dos valores previamente selecionados do input
     * toda vez que filtramos os itens.
     */

    //ainda não sei quais os impactos que isso pode causar, mas parece estar funcionando kkkk
    updateInernalAutocompleteSelectedItems() {
      if (this.isMounted) {
        const values = this.value

        if (Array.isArray(values) && values.length > 0) {
          const selectedValues = values.map((v) => {
            const itemObj = this.items.find(
              //TODO: tratar melhor isso
              (i) => (i[this.itemValue] || i) === v
            )

            return itemObj
          })

          this.$nextTick(() => {
            this.$refs.autocomplete.selectedItems = selectedValues
          })
        }
      }
    },

    onSearchInput(value) {
      /**
       * Evento disparado ao digitar no input de busca
       *
       * @property {string} value - Valor digitado no input
       */
      this.internalSearch = value
    },
    onChange(value) {
      /**
       * Evento disparado ao selecionar um item
       *
       * @property {any} value - Valor selecionado no input
       */
      this.$emit('change', value)
    },

    onBlur() {
      /**
       * Evento disparado ao sair do input
       */
      this.$emit('blur')
    },

    onToggleAll() {
      const filteredItems = this.$refs.autocomplete.filteredItems
      const selectedItems = this.$refs.autocomplete.selectedItems

      this.$nextTick(() => {
        if (this.hasSelectionInFilteredItems) {
          this.$emit(
            'change',
            selectedItems
              .filter((item) => !filteredItems.includes(item))
              .map((item) => item[this.itemValue])
          )
        } else {
          this.$emit(
            'change',
            [...selectedItems, ...filteredItems].map(
              (item) => item[this.itemValue]
            )
          )
        }
        this.$refs.autocomplete.lazySearch = ''
        this.$refs.autocomplete.$refs.menu.isActive = false
      })
    },

    // normaliza os itens buscados
    filterTextItems(item, queryText, itemText) {
      if (typeof this.filter == 'function') {
        return this.filter(item, queryText, itemText)
      }

      return itemText
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .includes(
          queryText
            .toLocaleLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
        )
    },
  },
  i18n: {
    messages: {
      pt: {
        no_data_available: 'Nenhum dado disponível',
        check_all: 'Selecionar todos',
        uncheck_all: 'Desmarcar todos',
      },
      en: {
        no_data_available: 'No data available',
        check_all: 'Select all',
        uncheck_all: 'Uncheck all',
      },
      es: {
        no_data_available: 'Datos no disponibles',
        check_all: 'Seleccionar todos',
        uncheck_all: 'Desmarcar todos',
      },
      fr: {
        no_data_available: 'Aucune donnée disponible',
        check_all: 'Tout sélectionner',
        uncheck_all: 'Tout désélectionner',
      },
      tr: {
        no_data_available: 'Veri mevcut değil',
        check_all: 'Hepsini seç',
        uncheck_all: 'Hepsini seçme',
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.v-list {
  padding: 0 !important;
}

.truncate {
  width: 100%;
  height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.zoox-autocomplete {
  border-radius: var(--component-input-md-radius) !important;
}

.zoox-select-all {
  display: flex;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--white);
  align-items: center;
  flex: 1 1 100%;
  padding: 0 16px;
  cursor: pointer;
  min-height: 40px;
  height: 40px;
  user-select: none;
  border-bottom: 1px solid #cecece;

  & > :first-child {
    margin-right: 32px;
    display: inline-flex;
    min-width: 24px;
    align-self: center;
  }

  & > :last-child {
    flex: 1 1;
    padding: 12px 0;
    display: flex;
    align-self: center;
    align-items: center;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>

<style lang="scss">
@use '@scss/global' as g;

.zoox-autocomplete {
  &.small {
    @include g.zoox-small-form-input;

    .v-select__selections {
      font-size: var(--zoox-font-size-body3);
    }

    &.chips {
      .v-select__selections {
        padding: 6px 0 0 2px !important;
      }
    }
  }
}
</style>
